<template>
  <div v-if="visible" class="tour-overlay">
    <div class="tour-highlight" :style="highlightStyle"></div>
    <div class="tour-popover" :style="popoverStyle">
      <div class="tour-header">
        <div class="tour-title">{{ currentTitle }}</div>
        <div class="tour-step">{{ currentIndex + 1 }} / {{ totalSteps }}</div>
      </div>
      <!-- Прогресс как в SmartSearchTour -->
      <div class="tour-progress">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
        </div>
        <div class="progress-steps">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="progress-step"
            :class="{ 'completed': index < currentIndex, 'current': index === currentIndex, 'pending': index > currentIndex }"
            :title="t(step.title)"
          >
            <span class="step-number">{{ index + 1 }}</span>
          </div>
        </div>
      </div>
      <div class="tour-text">{{ currentText }}</div>
      <div class="tour-actions">
        <button class="tour-btn secondary" @click="skip">{{ $t('onboarding.actions.skip') }}</button>
        <button v-if="currentIndex > 0" class="tour-btn secondary" @click="prev">{{ $t('onboarding.actions.prev') }}</button>
        <button v-if="!isLast" class="tour-btn primary" @click="next">{{ $t('onboarding.actions.next') }}</button>
        <button v-else class="tour-btn primary" @click="finish">{{ $t('onboarding.actions.finish') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { authState } from '@/store/auth';
import { markOnboardingStep } from '@/composables/useOnboarding';

export default {
  name: 'NavbarTour',
  props: {
    show: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const visible = ref(false);
    const highlightRect = ref({ top: 0, left: 0, width: 0, height: 0 });
    const currentIndex = ref(0);
    const steps = ref([]);
    const isAuthenticated = computed(() => authState.isAuthenticated);
    const prevHtmlOverflow = ref('');
    const prevBodyOverflow = ref('');
    const focusedEl = ref(null);

    const rawSteps = () => ([
      { selector: '.home-link', title: 'navbar.home', text: 'onboarding.navbar.items.home' },
      { selector: '.search-link', title: 'navbar.product_info', text: 'onboarding.navbar.items.search', authOnly: true },
      { selector: '.research-link', title: 'navbar.my_market_research', text: 'onboarding.navbar.items.research', authOnly: true },
      { selector: '.pricing-link', title: 'navbar.pricing', text: 'onboarding.navbar.items.pricing' },
      { selector: '.more-dropdown .more-button', title: 'onboarding.navbar.items.more_title', text: 'onboarding.navbar.items.more', authOnly: true },
      { selector: '.language-switcher-desktop .language-button-desktop', title: 'onboarding.navbar.items.lang_title', text: 'onboarding.navbar.items.lang' },
      { selector: '.settings-group .theme-toggle', title: 'onboarding.navbar.items.theme_title', text: 'onboarding.navbar.items.theme' },
    ]);

    const computeRect = () => {
      const step = steps.value[currentIndex.value];
      if (!step) return;
      const el = document.querySelector(step.selector);
      if (!el) return;
      // Подсветка реального элемента мягким свечением
      applyFocusClass(el);
      const rect = el.getBoundingClientRect();
      highlightRect.value = {
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16,
      };
      maybeOpenMenusForStep(step.selector);
    };

    const totalSteps = computed(() => steps.value.length);
    const progressPercentage = computed(() => ((currentIndex.value + 1) / Math.max(1, totalSteps.value)) * 100);
    const isLast = computed(() => currentIndex.value >= steps.value.length - 1);
    const currentTitle = computed(() => {
      const step = steps.value[currentIndex.value];
      return step ? t(step.title) : '';
    });
    const currentText = computed(() => {
      const step = steps.value[currentIndex.value];
      return step ? t(step.text) : '';
    });

    const highlightStyle = computed(() => ({
      top: `${highlightRect.value.top}px`,
      left: `${highlightRect.value.left}px`,
      width: `${highlightRect.value.width}px`,
      height: `${highlightRect.value.height}px`,
    }));

    const popoverStyle = computed(() => {
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = document.documentElement.clientHeight;
      const popoverWidth = 360;
      const popoverHeight = 220;

      // Ставим поповер под выделением; если не помещается, уводим вправо
      let topBelow = highlightRect.value.top + highlightRect.value.height + 12;
      let left = highlightRect.value.left + 12;
      let top = topBelow;

      if (topBelow + popoverHeight > viewportHeight - 16) {
        // Выше выделения
        top = Math.max(16, highlightRect.value.top - popoverHeight - 12);
      }

      // Контроль правого края
      if (left + popoverWidth > viewportWidth - 16) {
        left = viewportWidth - popoverWidth - 16;
      }
      if (left < 16) left = 16;

      return { position: 'fixed', top: `${top}px`, left: `${left}px`, zIndex: 1002 };
    });

    const buildSteps = () => {
      const list = rawSteps().filter(step => {
        if (step.authOnly && !isAuthenticated.value) return false;
        return !!document.querySelector(step.selector);
      });
      steps.value = list;
      if (currentIndex.value >= list.length) currentIndex.value = 0;
    };

    const open = async () => {
      buildSteps();
      if (!steps.value.length) return;
      await nextTick();
      computeRect();
      visible.value = true;
      // Блокируем вертикальный скролл страницы на время онбординга
      prevHtmlOverflow.value = document.documentElement.style.overflowY;
      prevBodyOverflow.value = document.body.style.overflowY;
      document.documentElement.style.overflowY = 'hidden';
      document.body.style.overflowY = 'hidden';
      window.addEventListener('resize', onResize);
      window.addEventListener('scroll', onResize, { passive: true });
    };

    const onResize = () => {
      // На ресайз/скролл пересчитываем по свежему DOM
      requestAnimationFrame(() => computeRect());
    };

    const close = () => {
      visible.value = false;
      // Восстанавливаем скролл страницы
      document.documentElement.style.overflowY = prevHtmlOverflow.value || '';
      document.body.style.overflowY = prevBodyOverflow.value || '';
      removeFocusClass();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize);
      emit('close');
    };

    const next = () => {
      if (currentIndex.value < steps.value.length - 1) {
        currentIndex.value += 1;
        computeRect();
      }
    };

    const prev = () => {
      if (currentIndex.value > 0) {
        currentIndex.value -= 1;
        computeRect();
      }
    };

    const skip = async () => {
      try {
        await markOnboardingStep('navbar', true);
      } finally {
        close();
      }
    };

    const finish = async () => {
      try {
        await markOnboardingStep('navbar', true);
      } finally {
        close();
      }
    };

    onMounted(() => {
      if (props.show) open();
    });

    watch(() => props.show, (val) => {
      if (val) open(); else if (visible.value) close();
    });

    const maybeOpenMenusForStep = (selector) => {
      // Авто-раскрытие дропдаунов для шага "Ещё" и "Язык"
      try {
        if (selector && selector.includes('.more-button')) {
          const btn = document.querySelector('.more-dropdown .more-button');
          if (btn) btn.click();
        }
        if (selector && selector.includes('.language-button-desktop')) {
          const btn = document.querySelector('.language-switcher-desktop .language-button-desktop');
          if (btn) btn.click();
        }
      } catch (_) {
        // ignore
      }
    };

    const applyFocusClass = (el) => {
      try {
        if (focusedEl.value && focusedEl.value !== el) {
          focusedEl.value.classList.remove('qp-tour-focus');
        }
        el.classList.add('qp-tour-focus');
        focusedEl.value = el;
      } catch (_) { /* ignore */ }
    };

    const removeFocusClass = () => {
      try {
        if (focusedEl.value) {
          focusedEl.value.classList.remove('qp-tour-focus');
          focusedEl.value = null;
        }
      } catch (_) { /* ignore */ }
    };

    onBeforeUnmount(() => {
      // На всякий случай возвращаем скролл
      document.documentElement.style.overflowY = prevHtmlOverflow.value || '';
      document.body.style.overflowY = prevBodyOverflow.value || '';
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize);
    });

    return {
      visible,
      currentIndex,
      totalSteps,
      progressPercentage,
      isLast,
      currentTitle,
      currentText,
      highlightStyle,
      popoverStyle,
      next,
      prev,
      skip,
      finish,
      close,
    };
  },
};
</script>

<style scoped>
.tour-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: transparent;
}

.tour-highlight {
  position: absolute;
  border-radius: 12px;
  box-shadow:
    0 0 0 9999px rgba(0,0,0,0.45),
    0 0 0 1.5px rgba(var(--primary-color-rgb), 0.9),
    0 16px 48px rgba(0,0,0,0.4),
    0 0 32px rgba(var(--primary-color-rgb), 0.3) inset;
  pointer-events: none;
  z-index: 1001;
  animation: tourPulse 1.8s ease-in-out infinite;
  transition: top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease;
}

.tour-popover {
  position: fixed;
  width: 360px;
  background: var(--theme-card-bg);
  color: var(--theme-text);
  border: 1px solid rgba(var(--theme-border-rgb), 0.4);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.35), 0 0 0 1px rgba(var(--theme-border-rgb), 0.4) inset;
  z-index: 1002;
  transition: top 0.2s ease, left 0.2s ease;
}

.tour-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.tour-step {
  font-size: 12px;
  font-weight: 700;
  background: rgba(var(--primary-color-rgb), 0.12);
  color: var(--primary-color);
  padding: 4px 8px;
  border-radius: 8px;
}

.tour-title {
  font-weight: 700;
}

.tour-text {
  margin-top: 8px;
  line-height: 1.4;
}

.tour-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.tour-btn {
  padding: 8px 12px;
  border-radius: 8px;
  font-weight: 600;
}

.tour-btn.primary {
  background: var(--primary-color);
  color: white;
}

.tour-btn.secondary {
  background: rgba(var(--theme-text-rgb), 0.06);
  color: var(--theme-text);
}

/* Прогресс как в SmartSearchTour */
.tour-progress {
  margin: 12px 0;
}
.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(var(--theme-text-rgb), 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 10px;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
  border-radius: 2px;
  transition: width 0.3s ease;
}
.progress-steps {
  display: flex;
  justify-content: space-between;
  gap: 4px;
}
.progress-step {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.progress-step.completed { background: var(--primary-color); color: #fff; }
.progress-step.current { background: var(--accent-color); color: #fff; transform: scale(1.15); box-shadow: 0 0 0 2px rgba(var(--accent-color-rgb), 0.25); }
.progress-step.pending { background: rgba(var(--theme-text-rgb), 0.1); color: rgba(var(--theme-text-rgb), 0.6); }

@keyframes tourPulse {
  0% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.45), 0 0 0 1.5px rgba(var(--primary-color-rgb), 0.6), 0 16px 48px rgba(0,0,0,0.4), 0 0 32px rgba(0,0,0,0) inset; }
  50% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.45), 0 0 0 3px rgba(var(--primary-color-rgb), 1), 0 24px 56px rgba(0,0,0,0.5), 0 0 32px rgba(var(--primary-color-rgb), 0.25) inset; }
  100% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.45), 0 0 0 1.5px rgba(var(--primary-color-rgb), 0.6), 0 16px 48px rgba(0,0,0,0.4), 0 0 32px rgba(0,0,0,0) inset; }
}

/* Мягкая подсветка самого элемента фокусировки */
.qp-tour-focus {
  position: relative;
  z-index: 1003 !important;
  outline: 0;
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.0); /* резерв */
}
.qp-tour-focus::after {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 12px;
  pointer-events: none;
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.8), 0 8px 24px rgba(0,0,0,0.25);
}
</style>


